import OT from "@opentok/client";
import { getDefaultDeviceID, getDeviceUpdateMethod, setDefaultDeviceID } from "../../../helpers";
import { MEDIA_DEVICE_STATUSES, VIDEO_MEMBER_TYPES } from "@/helpers/constants";
import { DEVICE_TYPE } from "@/modules/calls/enums";

export default {
  /** Check user has video device */
  async checkVideoDeviceAvailable({ commit }) {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasVideoDevice = !!devices.find(device => device.kind === "videoinput");
    commit("setVideoDeviceAvailable", hasVideoDevice);
  },
  /** Check if system supported by OT */
  async checkSystemSupported({ commit }) {
    const isSystemSupported = OT.checkSystemRequirements();
    commit("setSystemSupported", isSystemSupported);
  },
  async getAvailableDevices({ commit, getters, dispatch, rootState }) {
    OT.getDevices(async (err, devices) => {
      if (err) {
        await dispatch("videoChat/updateUserInfo", { mediaDeviceStatus: MEDIA_DEVICE_STATUSES.DENY }, { root: true });
        commit("videoChat/setMediaAccessError", err.name, { root: true });
        commit("videoChat/deleteVideoChatMember", rootState.users.user.user_uuid, { root: true });
        commit("videoChat/setVideoChats", [], { root: true });
      } else {
        commit("setSources", devices);

        commit("setSelectedAudioSource", getDefaultDeviceID(getters.audioSources, DEVICE_TYPE.AUDIO));
        commit("setSelectedVideoSource", getDefaultDeviceID(getters.videoSources, DEVICE_TYPE.VIDEO));

        await dispatch("videoChat/updateUserInfo", { mediaDeviceStatus: MEDIA_DEVICE_STATUSES.ALLOW }, { root: true });
        await dispatch("videoChat/getVideoChats", {}, { root: true });
        commit("videoChat/setMediaAccessError", null, { root: true });

        commit(
          "videoChat/setVideoChatMember",
          {
            userUUID: rootState.users.user.user_uuid,
            data: {
              memberType: VIDEO_MEMBER_TYPES.SELF,
              publisher: null,
              invite: null,
              stream: null
            }
          },
          { root: true }
        );
        commit("setReady", true);
      }
    });
  },
  /** Check permission to access devices */
  async getAvailableSources({ dispatch, commit }) {
    OT.getUserMedia()
      .then(async stream => {
        await dispatch("getAvailableDevices");

        // Stop the tracks so that we stop using this camera and microphone
        // If you don't do this then cycleVideo does not work on some Android devices
        stream.getTracks().forEach(track => track.stop());
        commit("setDeviceError", false);
      })
      .catch(error => {
        commit("setReady", false);
        commit("setDeviceError", error.name);
      });
  },
  /**
   * Update publisher source
   * @param commit
   * @param {Object} publisher: OT publisher
   * @param {String} deviceType: DEVICE_TYPE value
   * @param {String} source: string with device ID
   * @param {Boolean} [persist]: persist in local storage
   */
  // eslint-disable-next-line no-unused-vars
  updatePublisherSource({ commit }, { publisher, deviceType, source, persist }) {
    const updateMethod = getDeviceUpdateMethod(deviceType);

    publisher[updateMethod](source).catch(err => {
      throw new Error(`setAudioSource failed: ${err.message}`);
    });

    if (persist) setDefaultDeviceID(source, deviceType);
  },
  /** Prompt user for permission and get device list */
  async askVideoPermissionsAndDevices({ dispatch, getters }) {
    await dispatch("checkSystemSupported");
    await dispatch("checkVideoDeviceAvailable");
    if (getters.isSystemSupported) {
      await dispatch("getAvailableSources");
    }
  }
};
